import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import Navbar from '../home/components/Navbar';
import GeneralPanel from './components/GeneralPanel';
import NotificationPanel from './components/NotificationPanel';
import AppearancePanel from './components/AppearancePanel';
import DataPanel from './components/DataPanel';
import CustomerSettingsPanel from './components/CustomerSettingsPanel';
import OrderSettingsPanel from './components/OrderSettingsPanel';
import MembershipPanel from './components/MembershipPanel';

type Tab = 'general' | 'notification' | 'appearance' | 'data' | 'customer' | 'order' | 'membership';

const tabs: { key: Tab; label: string; icon: string; desc: string }[] = [
  { key: 'general',      label: '通用设置', icon: 'ri-settings-3-line',       desc: '语言、时区与基础偏好' },
  { key: 'notification', label: '通知设置', icon: 'ri-notification-3-line',   desc: '消息提醒与推送管理' },
  { key: 'appearance',   label: '界面外观', icon: 'ri-palette-line',           desc: '主题色、字号与布局' },
  { key: 'customer',     label: '客户设置', icon: 'ri-user-settings-line',     desc: '客户类型与标签管理' },
  { key: 'order',        label: '订单设置', icon: 'ri-file-list-3-line',       desc: '服务类型与预约行为' },
  { key: 'data',         label: '数据与隐私', icon: 'ri-shield-check-line',    desc: '导出数据与隐私选项' },
  { key: 'membership',   label: '会员特权', icon: 'ri-vip-crown-2-line',       desc: '会员等级与功能权限' },
];

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  // 管理员判定只认后端下发的 isAdmin（见 docs/接口契约.md），不在这里比对 role 字符串
  const { profile, user } = useAuthStore();
  const isAdmin = profile?.isAdmin ?? user?.isAdmin ?? false;
  // 对管理员收起「会员特权」：`usePermission().can()` 里 isAdmin 直接放行全部功能，
  // 等级对管理员不产生任何差异，留着这一格等于暗示高级功能要靠付费解锁。
  const visibleTabs = isAdmin ? tabs.filter(t => t.key !== 'membership') : tabs;

  // deep-link：?tab= 指定初始标签页（仅接受已知 key，否则回落 general）
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const t = searchParams.get('tab');
    return tabs.some((x) => x.key === t) ? (t as Tab) : 'general';
  });
  // 管理员即便带着 ?tab=membership 进来也不渲染那一格
  const currentTab = visibleTabs.some(t => t.key === activeTab) ? activeTab : 'general';

  // deep-link：?checkout=1&plan=CODE&ref=CODE 直达会员结算
  const autoCheckout = searchParams.get('checkout') === '1';
  const initialPlanCode = searchParams.get('plan');
  const initialReferralCode = searchParams.get('ref');

  const activeTabInfo = visibleTabs.find(t => t.key === currentTab)!;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-5 md:py-8 pb-24 md:pb-8">
        <div className="mb-5 md:mb-6">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-900">系统设置</h1>
          <p className="text-sm text-gray-500 mt-1">管理平台偏好、客户分类、订单行为与数据选项</p>
        </div>

        {/* Mobile: horizontal scrollable tabs */}
        <div className="flex md:hidden overflow-x-auto gap-1 pb-1 mb-4 scrollbar-hide">
          {visibleTabs.map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap shrink-0 transition-colors cursor-pointer ${
                currentTab === t.key
                  ? t.key === 'membership' ? 'bg-amber-500 text-white' : 'bg-teal-500 text-white'
                  : 'bg-white text-gray-600 border border-gray-200'
              }`}
            >
              <i className={`${t.icon} text-sm`} />
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start">
          {/* Sidebar - desktop only */}
          <div className="hidden md:block w-60 shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 p-2 space-y-1">
              {visibleTabs.map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  className={`w-full flex items-start gap-3 px-3 py-3 rounded-lg text-left transition-colors cursor-pointer ${
                    currentTab === t.key
                      ? t.key === 'membership' ? 'bg-amber-50 text-amber-600' : 'bg-teal-50 text-teal-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                    <i className={`${t.icon} text-base`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium whitespace-nowrap">{t.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5 leading-snug">{t.desc}</p>
                  </div>
                  {currentTab === t.key && (
                    <span className={`ml-auto mt-1 w-1.5 h-1.5 rounded-full ${t.key === 'membership' ? 'bg-amber-500' : 'bg-teal-500'} shrink-0`} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 bg-white rounded-xl border border-gray-100 p-5 md:p-8 min-h-[400px] md:min-h-[560px]">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900">{activeTabInfo.label}</h2>
              <p className="text-sm text-gray-500 mt-0.5">{activeTabInfo.desc}</p>
            </div>

            {currentTab === 'general'      && <GeneralPanel />}
            {currentTab === 'notification' && <NotificationPanel />}
            {currentTab === 'appearance'   && <AppearancePanel />}
            {currentTab === 'customer'     && <CustomerSettingsPanel />}
            {currentTab === 'order'        && <OrderSettingsPanel />}
            {currentTab === 'data'         && <DataPanel />}
            {currentTab === 'membership'   && (
              <MembershipPanel
                autoCheckout={autoCheckout}
                initialPlanCode={initialPlanCode}
                initialReferralCode={initialReferralCode}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
